import ElakayImage from '../../components/images/ElakayImage';
// <SwiperPub1500x400> -- the promotional banner at the top of shops (and of
// the global Layout when P1). Three cases, decided in this order:
//   A. `getpub` has images: the shop's (or category's) OWN promotional
//      images, shown as they are -- never replaced by Elakay's banners.
//   B. no own images: the Default Promotional Swiper published by Family in
//      Swiper Manager -> "Default Promotional Swipers" (target "shop" for
//      `shop`, "layout" for `P1`), resolved once by the server engine and
//      drawn by <DefaultPromoSwiper> (stable: no randomness on render).
//   C. nothing published (or the request failed): the historical banner,
//      kept as the fallback -- now stable across renders (random choices
//      memoized once per mount, texts always matched to the slides) and
//      without the fixed 600 ms loader (each slide's texts appear once its
//      own image has loaded).
// The "1500x400" name is historical: the real frame follows the images
// (1200x686 for the shop banners), see defaultPromoRegistry.json frameRatios.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { toImageUrl } from "../../config";
import img1500x600 from "../Layout/imgs/Untitled-2.jpg";
import pubLoader from "../Layout/imgs/loading3.gif";
import SliderElakay from "../pageInside/sliderElakayImage";
import ThreeDText from "../pageInside/threedtext/threeDtext";
import { generateThreeDOptions, selectThreeDByY } from "../pageInside/threedtext/threeD-random";
import colorMatric from "../pageInside/threedtext/threedtext-colors.json";
import componentThreeDText from "../pageInside/threedtext/threedtext-matrix.json";
import DefaultPromoSwiper from "../defaultPromo/DefaultPromoSwiper";
import useDefaultPromo from "../defaultPromo/useDefaultPromo";
import "../defaultPromo/DefaultPromo.css";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import useElakaySwiper from "./swiperSettings/useElakaySwiper";

// Historical texts ("Yvox" = the shop name). Still used by the fallback
// banner, and imported once into the Text Library by the migration
// (Swiper Manager -> Default Promotional Swipers -> Import historical banners).
const textes = [
  {
    titre1: "We Are Lakay Yvox",
    paragraph1: "Build, share, buy.; together.",
    titre2: "Welcome to Elakay",
    paragraph2: "Buy, sell.; build your dreams."
  },
  {
    titre1: "Welcome to Lakay Yvox",
    paragraph1: "Everyone belongs.; grow together.",
    titre2: "Follow Favorite Shops",
    paragraph2: "Follow sellers.; stay updated."
  },
  {
    titre1: "Build Together in Yvox",
    paragraph1: "Creators, dreamers.; unite here.",
    titre2: "Support Local Creators",
    paragraph2: "Every purchase.; helps families grow."
  },
  {
    titre1: "Join the Yvox Family",
    paragraph1: "Creativity, support.; and progress.",
    titre2: "Buy With Confidence",
    paragraph2: "Verified products.; secure delivery."
  },
  {
    titre1: "Inside the Yvox World",
    paragraph1: "Innovation, love.; opportunity meet here.",
    titre2: "Share the Love",
    paragraph2: "Invite friends.; grow together."
  },
  {
    titre1: "Grow Together in Yvox",
    paragraph1: "Support.; believe in each other.",
    titre2: "Create an Account",
    paragraph2: "Join us.; explore today."
  },
  {
    titre1: "You're Home in Yvox",
    paragraph1: "Unity, trust.; creativity for all.",
    titre2: "Welcome to the Family",
    paragraph2: "Support family.; buy to uplift."
  },
  {
    titre1: "Live the Yvox Spirit",
    paragraph1: "Creation, sharing.; community for all.",
    titre2: "Follow Your Passion",
    paragraph2: "Tools to grow.; your business."
  },
  {
    titre1: "Our Home Is Yvox",
    paragraph1: "More than a market.; it's family.",
    titre2: "Support Your Community",
    paragraph2: "Together, stronger.; connected family."
  },
  {
    titre1: "We Belong in Yvox",
    paragraph1: "Every voice.; builds our future.",
    titre2: "Buy Smart, Live Better",
    paragraph2: "Everything you need.; best price."
  },
  {
    titre1: "Discover Yvox in Elakay",
    paragraph1: "A unique shop.; creativity, fashion, tech.",
    titre2: "Share Your Experience",
    paragraph2: "Tell others.; inspire them to join."
  },
  {
    titre1: "Build Your Yvox Brand",
    paragraph1: "Create, design.; grow your shop.",
    titre2: "Create and Build",
    paragraph2: "Build your brand.; your legacy."
  },
  {
    titre1: "Yvox, Boutique of Elakay",
    paragraph1: "A special place.; every voice matters.",
    titre2: "Welcome to Yvox World",
    paragraph2: "A global market.; every voice heard."
  },
  {
    titre1: "Connect with Yvox Sellers",
    paragraph1: "Follow favorites.; stay inspired.",
    titre2: "Follow, Like, Connect",
    paragraph2: "Stay connected.; never miss a deal."
  },
  {
    titre1: "Empower Others with Yvox",
    paragraph1: "Support creators.; help communities thrive.",
    titre2: "Support and Empower",
    paragraph2: "Support others.; build a network."
  },
  {
    titre1: "Inspire Through Yvox",
    paragraph1: "Your choices.; inspire innovation.",
    titre2: "Buy to Inspire",
    paragraph2: "Your purchases.; spark new ideas."
  },
  {
    titre1: "Grow Together with Yvox",
    paragraph1: "Share.; grow your business.",
    titre2: "Share to Grow",
    paragraph2: "Every share.; opens opportunities."
  },
  {
    titre1: "Your Space in Yvox",
    paragraph1: "Launch your shop.; go global.",
    titre2: "Create Your Own Space",
    paragraph2: "Start your shop.; make your mark."
  },
  {
    titre1: "Welcome Home to Yvox",
    paragraph1: "A warm space.; family meets dreams.",
    titre2: "Welcome Home",
    paragraph2: "More than a market.; it's family."
  },
  {
    titre1: "Dream Big with Yvox",
    paragraph1: "Creators succeed.; in our network.",
    titre2: "Follow Your Dreams",
    paragraph2: "Believe, build.; succeed together."
  },
  {
    titre1: "Honest Trade in Yvox",
    paragraph1: "Support ethical.; and local brands.",
    titre2: "Support Fair Trade",
    paragraph2: "Choose ethical shopping.; support people."
  },
  {
    titre1: "Shop with Purpose, Yvox",
    paragraph1: "Every purchase.; supports innovation.",
    titre2: "Buy with Purpose",
    paragraph2: "Every purchase.; a meaningful mission."
  },
  {
    titre1: "Share Your Story, Yvox",
    paragraph1: "Tell how Yvox.; helped you grow.",
    titre2: "Share Your Story",
    paragraph2: "Tell your journey.; inspire others."
  },
  {
    titre1: "Connect Through Yvox",
    paragraph1: "Every interaction.; strengthens our network.",
    titre2: "Create a Connection",
    paragraph2: "Every interaction.; grows your impact."
  },
  {
    titre1: "Explore Your Yvox Market",
    paragraph1: "Innovation, people.; and style, closer.",
    titre2: "Welcome to Your Marketplace",
    paragraph2: "Find what you love.; help others."
  },
  {
    titre1: "Yvox Keeps Growing",
    paragraph1: "Grows with every.; entrepreneur and buyer.",
    titre2: "Follow Growth",
    paragraph2: "Elakay evolves.; with every user."
  },
  {
    titre1: "Invest in Yvox",
    paragraph1: "Create, buy.; collaborate with us.",
    titre2: "Support Your Future",
    paragraph2: "Every action.; builds tomorrow."
  },
  {
    titre1: "Buy, Sell, Connect, Yvox",
    paragraph1: "Seamless commerce.; trusted boutique.",
    titre2: "Buy, Sell, Connect",
    paragraph2: "Trade and connect.; with ease."
  },
  {
    titre1: "Share the Vision, Yvox",
    paragraph1: "Join a movement.; inspiring the world.",
    titre2: "Share the Vision",
    paragraph2: "Creativity and collaboration.; for all."
  },
  {
    titre1: "Better World with Yvox",
    paragraph1: "Ideas into action.; dreams into reality.",
    titre2: "Create a Better World",
    paragraph2: "Ideas into action.; dreams into reality."
  }
];

export const LEGACY_PROMO_TEXTS = textes;

const replaceYvox = (list, newName) => {
  if (!Array.isArray(list)) return [];
  const name = newName || "Elakay";
  return list.map((item) => {
    const newItem = { ...item };
    Object.keys(newItem).forEach((key) => {
      if (typeof newItem[key] === "string") newItem[key] = newItem[key].replace(/Yvox/g, name);
    });
    return newItem;
  });
};

const getRandomTexts = (list, count = 3) => {
  if (!Array.isArray(list)) return [];
  const shuffled = [...list].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

// Fluid size between min (320 px viewport) and max (1920 px viewport).
const fluidSize = (width, minSize, maxSize, minWidth = 320, maxWidth = 1920) => {
  const clamped = Math.max(minWidth, Math.min(width, maxWidth));
  return minSize + ((clamped - minWidth) / (maxWidth - minWidth)) * (maxSize - minSize);
};

// ONE resize listener for every text size (was one per size + a timer each).
const useViewportWidth = () => {
  const [width, setWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return width;
};

// Carte "glassmorphism" : fond flouté + bordure fine + ombre douce + accent latéral
const glassBg = (accent, maxWidth = "50%") => ({ type: "glass", color: "#ffffff", opacity: 0.10, accent, maxWidth });

const legacyImageStyle = { width: "100%", maxHeight: "600px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)" };
const onLegacyImageError = (event) => {
  event.target.onerror = null;
  event.target.src = img1500x600;
};

// ── Frame ────────────────────────────────────────────────────────────
// A slide only draws its image while it is the visible one, and an image has
// no height before it has loaded: without a frame the banner collapsed and
// grew back on every slide change, moving the whole page below it. The frame
// keeps the ratio of the last image drawn (remembered per page for the
// session), so the height is known from the first render and only changes
// when an image of another ratio really arrives.
const HISTORICAL_FRAME_RATIO = 1200 / 686; // every historical banner (shop and layout)
const OWN_FRAME_RATIO = 1500 / 500; // Elakay's own home banners (Routes.js)
const frameRatios = new Map();
const sameRatio = (a, b) => Math.abs(a - b) / b < 0.01;
// Routes.js shows this animated loader as the only "image" until the page's
// promotional images are known: it is drawn inside the frame, never sizes it.
const isPubLoader = (url) => url === pubLoader;

const useBannerFrame = (kind, fallbackRatio) => {
  const key = `${kind}|${typeof window !== "undefined" ? window.location.pathname : ""}`;
  const [, redraw] = useState(0);
  const ratio = frameRatios.get(key) || fallbackRatio;
  const onImageLoad = useCallback((event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget || {};
    if (!naturalWidth || !naturalHeight) return;
    const next = naturalWidth / naturalHeight;
    if (sameRatio(frameRatios.get(key) || fallbackRatio, next)) return;
    frameRatios.set(key, next);
    redraw((count) => count + 1);
  }, [key, fallbackRatio]);
  return { ratio, onImageLoad };
};

// ── Case C: the historical banner (fallback) ─────────────────────────
const useLegacyComposition = (infoSource, shopName, count) => {
  // Generated ONCE per mount: a re-render never reshuffles the texts or the
  // 3D variants any more -- nor does the shop's name arriving after the
  // first render (it is only written into the sentences already chosen).
  const drawn = useMemo(() => {
    const texts = getRandomTexts(textes, Math.max(3, count));
    const opts = [
      generateThreeDOptions(colorMatric.colorMatrix, componentThreeDText, 3, {
        infoSource, variantModes: ["base"], in: ["orange3D", "neonGlow", "bubbleGum", "gradientOutline", "halftoneComic", "glassy"],
      }),
      generateThreeDOptions(colorMatric.colorMatrix, componentThreeDText, 3, {
        infoSource,
        variantModes: ["example"],
        in: ["orange3D:playfairDisplay", "orange3D:cinzel", "orange3D:abrilFatface", "orange3D:bebasNeue", "orange3D:prata", "orange3D:cormorantGaramond", "orange3D:oswald", "orange3D:quicksand", "neonGlow:playfairDisplay", "neonGlow:cinzel", "neonGlow:abrilFatface", "neonGlow:bebasNeue", "neonGlow:prata", "neonGlow:cormorantGaramond", "neonGlow:oswald", "neonGlow:quicksand"],
      }),
      generateThreeDOptions(colorMatric.colorMatrix, componentThreeDText, 3, {
        infoSource,
        variantModes: ["example"],
        in: ["neonGlow:playfairDisplay", "bubbleGum:playfairDisplay", "gradientOutline:playfairDisplay", "halftoneComic:playfairDisplay", "neonGlow:cinzel", "bubbleGum:cinzel", "gradientOutline:cinzel", "halftoneComic:cinzel", "neonGlow:bebasNeue", "bubbleGum:bebasNeue", "gradientOutline:bebasNeue"],
      }),
      generateThreeDOptions(colorMatric.colorMatrix, componentThreeDText, 3, {
        infoSource, variantModes: ["font"], in: ["cinzel", "abrilFatface", "bebasNeue", "prata", "cormorantGaramond", "oswald", "quicksand"],
      }),
    ];
    return { texts, opts };
    // infoSource only feeds the translation helper of the generator.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);
  return useMemo(() => ({ texts: replaceYvox(drawn.texts, shopName), opts: drawn.opts }), [drawn, shopName]);
};

const LegacyShopSlide = ({ src, index, legacy, sizes, position1, imagesOnly, onImageLoad }) => {
  const [loaded, setLoaded] = useState(false);
  // Texts always exist for every slide (index wraps), never undefined.
  const entry = legacy.texts.length ? legacy.texts[index % legacy.texts.length] : null;
  const [pick, pick1, pick2, pick3] = legacy.opts.map((options) => selectThreeDByY(options, index));
  return (
    <>
      <ElakayImage preset="banner" src={toImageUrl(src.link)} alt={`${src.name}`} onError={onLegacyImageError} onLoad={(event) => { setLoaded(true); onImageLoad?.(event); }} style={legacyImageStyle} />
      {loaded && entry && !imagesOnly && (
        <>
          <ThreeDText text={entry.titre2} variant={pick.variant} colors={pick.colors} size={sizes.title} placement="topLeft" reflect={false} background={glassBg(pick.colors.color, "52%")} />
          <ThreeDText text={entry.paragraph2} variant={pick1.variant} colors={pick1.colors} reflect={false} size={sizes.text1} textAlign="left" position={position1} background={glassBg(pick1.colors.color, "46%")} />
          <ThreeDText text={entry.titre1} variant={pick2.variant} colors={pick2.colors} size={sizes.text2} placement="centerRight" textAlign="right" zIndex={1000} reflect={false} background={glassBg(pick2.colors.color, "52%")} />
          <ThreeDText text={entry.paragraph1} variant={pick3.variant} colors={pick3.colors} style={{ color: pick3.colors.color }} tune={{ glowK: 0.22, blurK: 0.14 }} size={sizes.text3} placement="bottomRight" background={glassBg(pick3.colors.color, "46%")} textAlign="left" reflect={false} />
        </>
      )}
    </>
  );
};

const SwiperPub1500x400 = ({
  SliderPunShow = true,
  categoriesID,
  swiperOptions,
  getpub = [],
  infoSource,
  P1 = false,
  shop = false,
  minSize = 20,
  maxSize = 90,
  text1 = null,
  text2 = null,
  position1 = { position: "absolute", bottom: "30%", left: 18 },
}) => {
  const [visibleIndex, setVisibleIndex] = useState(0);
  // null = the page's own pub is not known yet: nothing is shown (as before).
  const pending = getpub === null;
  const ownPub = Array.isArray(getpub) ? getpub.filter((image) => typeof image?.urlImg === "string" && image.urlImg.trim()) : [];
  const hasOwnPub = ownPub.length > 0;
  const target = shop ? "shop" : "layout";
  // Case B is only looked up when the page has no promotional images of its
  // own (no request at all otherwise).
  // "Different per shop" variety is keyed by the shop's URL: stable from the
  // first render (the shop NAME arrives later -- a placeholder first -- and
  // would fetch and swap a second composition).
  const promoSalt = shop && typeof window !== "undefined" ? window.location.pathname : "";
  const promo = useDefaultPromo({ target, salt: promoSalt, enabled: !pending && !hasOwnPub && (shop || P1) });
  const legacyActive = !hasOwnPub && promo.status === "none" && promo.data?.fallback !== "hidden";
  const { randomImages, shopImages } = SliderElakay(infoSource, { enabled: legacyActive });
  const imagesOnly = promo.data?.fallback === "imagesOnly";
  const legacy = useLegacyComposition(infoSource, text2, shopImages?.length || 3);
  const viewport = useViewportWidth();
  const sizes = {
    title: fluidSize(viewport, minSize, maxSize),
    text1: fluidSize(viewport, 13, 55),
    text2: fluidSize(viewport, 15, 55),
    text3: fluidSize(viewport, 15, 40),
  };

  // Options: page instance > Animation Settings -> Swiper Settings > origin.
  const legacySlides = legacyActive ? (shop ? shopImages : (P1 ? randomImages : [])) : [];
  const pubSlideCount = hasOwnPub ? ownPub.length : legacySlides?.length || 0;
  const swp = useElakaySwiper("promoBanner", {
    swiperOptions,
    autoNavigation: Boolean(infoSource?.navigation),
    slideCount: pubSlideCount,
  });
  const frame = useBannerFrame(hasOwnPub ? "own" : "historical", hasOwnPub ? OWN_FRAME_RATIO : HISTORICAL_FRAME_RATIO);
  // The historical slides are drawn in an effect (SliderElakay), one render
  // after the fallback is chosen: the placeholder stays for that render.
  const [legacyDrawn, setLegacyDrawn] = useState(false);
  useEffect(() => { if (legacyActive) setLegacyDrawn(true); }, [legacyActive]);

  if (pending) return null;
  if (!hasOwnPub && (shop || P1)) {
    if (promo.status === "loading") {
      // Reserve the banner's height until the composition is known (no
      // layout jump, no arbitrary timer).
      return <div className="dpromo-placeholder" aria-busy="true" data-testid="default-promo-loading" />;
    }
    if (promo.status === "ready") {
      return (
        <DefaultPromoSwiper
          data={promo.data}
          shopName={text2 || ""}
          swiperOptions={swiperOptions}
          eager
          label={shop ? (text2 || undefined) : undefined}
        />
      );
    }
    if (!legacyActive) return null;
    if (!pubSlideCount && !legacyDrawn) return <div className="dpromo-placeholder" aria-busy="true" data-testid="default-promo-loading" />;
  }
  if (!pubSlideCount) return null;

  return (
    <Swiper
      onSlideChange={(swiper) => setVisibleIndex(swiper.realIndex)}
      key={swp.key}
      {...swp.swiperProps}
      className={`mySwiper elk-promo-banner ${swp.className}`}
      /* "mySwiper" est un nom de classe générique repris tel quel (copié du
         tutoriel officiel Swiper) dans une dizaine de composants slider du
         projet -- plusieurs définissent leur PROPRE `.mySwiper { height:
         ... }` (100%, 20%, 300px selon le fichier). Comme ce composant est
         rendu par Layout.js sur TOUTE page (pas seulement Home), toute
         collision de classe avec un AUTRE de ces sliders présent sur la
         même page fait gonfler ce swiper à une hauteur ambiante aberrante
         (vérifié en live : plusieurs milliers de px), cassant le calcul de
         hauteur de tout le Body et poussant le Footer bien avant la fin
         réelle du contenu. Un style inline bat toujours une règle externe
         (sauf !important) -- on fixe donc ici, une fois pour toutes, la
         hauteur réelle de CE swiper (celle de son image, ~600px) quelle
         que soit la classe globale qui gagne par ailleurs. */
      style={{ height: "auto", minHeight: 0, maxHeight: legacyImageStyle.maxHeight, "--elk-promo-ratio": frame.ratio, ...swp.style }}
    >
      {hasOwnPub && ownPub.map((each, index) => (
        <SwiperSlide key={each._id || each.urlImg || index}>
          {index === visibleIndex && <ElakayImage
            preset="banner"
            src={toImageUrl(each.urlImg)}
            alt={each.urlImg}
            loading={index === 0 ? "eager" : "lazy"}
            decoding="async"
            onError={onLegacyImageError}
            onLoad={isPubLoader(each.urlImg) ? undefined : frame.onImageLoad}
            className={isPubLoader(each.urlImg) ? "elk-promo-loader" : undefined}
            style={isPubLoader(each.urlImg) ? undefined : legacyImageStyle}
          />}
        </SwiperSlide>
      ))}
      {!hasOwnPub && P1 && !shop && legacySlides.map((src, index) => (
        <SwiperSlide key={src.name || index}>
          {index === visibleIndex && <ElakayImage preset="banner" src={toImageUrl(src.link)} alt={`${src.name}`} loading={index === 0 ? "eager" : "lazy"} onError={onLegacyImageError} onLoad={frame.onImageLoad} style={legacyImageStyle} />}
        </SwiperSlide>
      ))}
      {!hasOwnPub && shop && legacySlides.map((src, index) => (
        <SwiperSlide key={src.name || index}>
          {index === visibleIndex && <LegacyShopSlide src={src} index={index} legacy={legacy} sizes={sizes} position1={position1} imagesOnly={imagesOnly} onImageLoad={frame.onImageLoad} />}
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default SwiperPub1500x400;
